import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ThemeProvider } from "@mui/material";
import { FEATURES, hasFeatureAccess } from "../helpers/featureAccess";
import { tipsRequest } from "../helpers/tipsWorkflow";
import { workareaTheme } from "../helpers/workareaTheme";
import StatisticModelComparison from "./StatisticModelComparison";
import StatisticModelFixture from "./StatisticModelFixture";
import StatisticModelTables from "./StatisticModelTables";
import LeagueChoice from "./LeagueChoice";
import TeamLeagueWorkspace from "./TeamLeagueWorkspace";
import { canUseTeamWorkspace } from "../helpers/teamWorkspace";
import "./StatisticModel.css";
export default function StatisticModel(props) {
  if (!hasFeatureAccess(FEATURES.STATISTICS_MODEL, props.user)) return null;
  return (
    <ThemeProvider theme={workareaTheme}>
      <Workspace {...props} />
    </ThemeProvider>
  );
}
function Workspace({ user, leagues = [], request = tipsRequest }) {
  const [teamWorkspaceOpen, setTeamWorkspaceOpen] = useState(false);
  const { i18n } = useTranslation(),
    en = i18n.language?.startsWith("en"),
    t = (de, eng) => (en ? eng : de);
  const [league, setLeague] = useState(""),
    [season, setSeason] = useState(""),
    [seasons, setSeasons] = useState([]),
    [data, setData] = useState(null),
    [state, setState] = useState(null),
    [fixture, setFixture] = useState(""),
    [result, setResult] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [progress, setProgress] = useState(""),
    [workspace, setWorkspace] = useState(null);
  const rebuildId = useRef(null);
  const generation = useRef(0),
    stop = useRef(false);
  useEffect(
    () => () => {
      stop.current = true;
      generation.current++;
    },
    []
  );
  async function action(fn) {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  const call = (mode, extra = {}) =>
    request("statistic-model", {
      mode,
      league_id: Number(league),
      season_id: Number(season),
      ...extra,
    });
  async function chooseLeague(id) {
    const g = ++generation.current;
    setLeague(id);
    setSeason("");
    setData(null);
    setState(null);
    setResult(null);
    setSeasons([]);
    setWorkspace(null);
    await action(async () => {
      const r = await request("statistic-model", {
        mode: "catalog",
        league_id: Number(id),
      });
      if (g === generation.current)
        setSeasons(r.seasons.filter((s) => s.is_current));
    });
  }
  async function chooseSeason(id) {
    const g = ++generation.current;
    setSeason(id);
    setData(null);
    setState(null);
    setResult(null);
    setFixture("");
    setWorkspace(null);
    setProgress("");
    await action(async () => {
      const body = { league_id: Number(league), season_id: Number(id) };
      const [d, s] = await Promise.all([
        request("statistic-model", { ...body, mode: "data" }),
        request("statistic-model", { ...body, mode: "status" }),
      ]);
      if (g === generation.current) {
        setData(d);
        setState(s.state);
        if (s.state) {
          const w = await request("statistic-model", {
            ...body,
            mode: "workspace",
          });
          if (g === generation.current) setWorkspace(w);
        }
      }
    });
  }
  async function rebuild() {
    if (
      !window.confirm(
        t(
          "Statistikmodell neu aufbauen? Bisherige Pre-Match-Werte bleiben im alten Modelllauf erhalten. Das Ergebnismodell bleibt unverändert.",
          "Rebuild statistics model? Previous pre-match records remain in the old run. The result model is unchanged."
        )
      )
    )
      return;
    rebuildId.current ||= crypto.randomUUID();
    await action(async () => {
      const r = await call("rebuild", { rebuild_id: rebuildId.current });
      setState(r.state);
      setResult(null);
      setWorkspace(await call("workspace"));
      setProgress("");
      rebuildId.current = null;
    });
  }
  async function calculateUpcoming() {
    await action(async () => {
      const rows = data.fixtures
        .filter((f) => !f.final && Date.parse(f.kickoff) > Date.now())
        .slice(0, 50);
      for (const f of rows) await call("predict", { fixture_id: f.id });
      setWorkspace(await call("workspace"));
    });
  }
  async function replay() {
    stop.current = false;
    await action(async () => {
      await call("refresh");
      for (let i = 0; i < 1200 && !stop.current; i++) {
        const r = await call("step");
        setState(r.state);
        setProgress(
          `${r.state.evaluated} ${t(
            "Spiele ausgewertet bis",
            "matches evaluated through"
          )} ${r.state.last_day}`
        );
        if (r.blocked) throw Error(r.blocked + ": " + r.fixture_ids.join(", "));
        if (r.results?.length) setResult(r.results.at(-1));
        if (r.done) {
          setWorkspace(await call("workspace"));
          return;
        }
      }
      if (!stop.current) throw Error("REPLAY_LIMIT");
    });
  }
  const f = data?.fixtures?.find((f) => String(f.id) === fixture),
    fmt = (n) =>
      Number.isFinite(n)
        ? n.toLocaleString(en ? "en-GB" : "de-DE", { maximumFractionDigits: 2 })
        : "—";
  return (
    <section className="statistic-model">
      <h2>{t("Statistikbasiertes Modell", "Statistics-based model")}</h2>
      <p>
        {t(
          "Eigenständiges Modell. Historische Berechnungen sind Simulationen; bestehende Prognosen und Team Settings werden nicht verändert.",
          "Independent model. Historical calculations are simulations; existing predictions and team settings are unchanged."
        )}
      </p>
      {canUseTeamWorkspace(user) && <div className="sm-controls">
        <button onClick={() => setTeamWorkspaceOpen(true)}>{t("Team- und Liga-Arbeitsbereich öffnen", "Open team and league workspace")}</button>
        <p>{t("Beide Modelle vergleichen, persönliche Settings prüfen und Änderungen vor dem Speichern ansehen.", "Compare both models, review personal settings and preview changes before saving.")}</p>
      </div>}
      {teamWorkspaceOpen && <TeamLeagueWorkspace user={user} leagues={leagues} request={request} initialLeague={leagues.find(l => Number(l.id) === Number(league)) || null} initialSeason={season || null} onClose={() => setTeamWorkspaceOpen(false)} />}
      <div className="sm-controls">
        <LeagueChoice
          leagues={leagues}
          value={league}
          onChange={chooseLeague}
          label={t("Liga", "League")}
          placeholder={t("Liga wählen", "Choose league")}
          disabled={busy}
        />
        <label>
          {t("Saison", "Season")}{" "}
          <select
            aria-label={t("Saison", "Season")}
            disabled={busy || !seasons.length}
            value={season}
            onChange={(e) => chooseSeason(e.target.value)}
          >
            <option value="">—</option>
            {seasons.map((s) => (
              <option key={s.season_id} value={s.season_id}>
                {s.name || s.season_id}
              </option>
            ))}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {data && (
        <>
          <p>
            {t("Datenstatus", "Data status")}: {data.status.finished}{" "}
            {t("abgeschlossen", "finished")} · {data.status.with_statistics}{" "}
            {t("mit Statistik", "with statistics")} · {data.status.complete}{" "}
            {t("vollständig", "complete")} · {data.status.partial}{" "}
            {t("teilweise", "partial")} · {data.status.without_statistics}{" "}
            {t("ohne Statistik", "without statistics")} ·{" "}
            {t("modellfähige Spieldaten", "model-ready match data")}:{" "}
            {data.status.model_ready ? t("ja", "yes") : t("nein", "no")}
          </p>
          <div className="sm-controls">
            <button
              disabled={busy || !!state}
              onClick={() =>
                action(async () => {
                  setState((await call("initialize")).state);
                  setWorkspace(await call("workspace"));
                })
              }
            >
              {t("Initialisieren", "Initialize")}
            </button>
            <button disabled={busy || !state} onClick={rebuild}>
              {t("Neuaufbau", "Rebuild")}
            </button>
            <button disabled={busy || !state} onClick={replay}>
              {t(
                "Chronologischer Test / Auswertung",
                "Chronological test / evaluation"
              )}
            </button>
            {busy && (
              <button
                onClick={() => {
                  stop.current = true;
                }}
              >
                {t("Nach laufendem Schritt stoppen", "Stop after current step")}
              </button>
            )}
          </div>
          {!!data?.status?.excluded_nonfinal_in_ended_season?.length && (
            <p>
              {t(
                "Nicht regulär abgeschlossene Spiele ausgeschlossen",
                "Non-final matches in ended season excluded"
              )}
              :{" "}
              {data.status.excluded_nonfinal_in_ended_season
                .map((f) => `${f.fixture_id} (${f.provider_state_id})`)
                .join(", ")}
            </p>
          )}
          {state && (
            <p>
              {state.model} · {t("Seed-Saison", "Seed season")}{" "}
              {state.seed_season_id} ·{" "}
              {state.initialization_fallback === "previous_model_end_state"
                ? t(
                    "Endzustand der Vorsaison übernommen",
                    "Previous season end state reused"
                  )
                : state.initialization_fallback === "one_prior_season"
                ? t("Start aus einer Vorsaison", "Seed from one prior season")
                : t(
                    "Ältere Saison + chronologischer Vorlauf",
                    "Older season + chronological warm-up"
                  )}{" "}
              · {t("Statistik-Faktor", "Statistics factor")}{" "}
              {fmt(state.parameters.statistics_update_factor)} ·{" "}
              {t("Conversion-Faktor", "Conversion factor")}{" "}
              {fmt(state.parameters.conversion_update_factor)}
            </p>
          )}
          <p>
            {t("Modellstatus", "Model status")}:{" "}
            {state
              ? t("initialisiert", "initialized")
              : t("nicht initialisiert", "not initialized")}
            {state && (
              <>
                {" "}
                · {t(
                  "Diese Saison eingearbeitet",
                  "Processed this season"
                )}: {state.evaluated_by_season?.[season] || 0} /{" "}
                {data.status.finished} · {t("Stand", "Through")}:{" "}
                {state.last_day}
              </>
            )}
          </p>
          <p aria-live="polite">{progress}</p>
          <button
            disabled={
              busy || !state || state.current_season_id !== Number(season)
            }
            onClick={calculateUpcoming}
          >
            {t("Nächste 50 Fixtures berechnen", "Calculate next 50 fixtures")}
          </button>
          <div className="sm-controls">
            <label>
              Fixture{" "}
              <select
                aria-label="Fixture"
                value={fixture}
                disabled={busy}
                onChange={(e) => {
                  setFixture(e.target.value);
                  setResult(null);
                }}
              >
                <option value="">—</option>
                {data.fixtures.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.kickoff.slice(0, 10)} · {f.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              disabled={busy || !state || !f}
              onClick={() =>
                action(async () => {
                  const r = await call(f.final ? "result" : "predict", {
                    fixture_id: f.id,
                  });
                  setResult(r.result);
                  setWorkspace(await call("workspace"));
                  if (!r.result)
                    setError(
                      t(
                        "Zuerst chronologisch auswerten.",
                        "Run chronological evaluation first."
                      )
                    );
                })
              }
            >
              {t("Berechnen / Ergebnis laden", "Calculate / load result")}
            </button>
          </div>
        </>
      )}
      <StatisticModelComparison
        key={`comparison-${season}`}
        comparison={workspace?.comparison}
        en={en}
      />
      {workspace && (
        <StatisticModelTables
          key={season}
          workspace={workspace}
          data={data}
          en={en}
          onSelect={(r) => {
            setResult(r);
            setFixture(String(r.pre.fixture_id));
            setTimeout(
              () =>
                document
                  .getElementById("sm-fixture-detail")
                  ?.scrollIntoView?.({ behavior: "smooth" }),
              0
            );
          }}
        />
      )}
      {result?.pre?.probability_status ===
        "season_parameter_record_missing" && (
        <p role="status">
          {t(
            "Season-Parameter-Record fehlt: 1X2-Werte nicht verfügbar.",
            "Season parameter record missing: 1X2 values unavailable."
          )}
        </p>
      )}
      {result && (
        <StatisticModelFixture
          key={result.pre.fixture_id}
          result={result}
          fixture={data?.fixtures?.find(
            (row) => row.id === result.pre.fixture_id
          )}
          comparison={workspace?.comparison?.rows?.find(
            (row) => row.fixture_id === result.pre.fixture_id
          )}
          leagueName={
            leagues.find((row) => Number(row.id) === Number(league))?.name
          }
          seasonName={
            seasons.find((row) => row.season_id === Number(season))?.name
          }
          en={en}
        />
      )}
    </section>
  );
}
